/**
 * Z-Index Layer System
 *
 * Defines the stacking order for all overlays and UI elements on the
 * quickstart player page. When adding a new element, pick the layer
 * that matches its responsibility and use the exported constant.
 *
 * Layers are ordered from bottom (closest to the stream) to top.
 * Within a layer, DOM order determines paint order (later = on top).
 *
 * ─────────────────────────────────────────────────────────────────────
 * LAYER                  Z-INDEX   DESCRIPTION
 * ─────────────────────────────────────────────────────────────────────
 * BACKGROUND             0         Page-level backgrounds
 * STREAM                 10        Game video stream, loading content
 * LOADING_UI             20-25     Loading-phase chrome
 * STREAM_STATUS          50        Pause icon, fullscreen notification
 * RECONNECT              60        Reconnection spinner
 * DESKTOP_ATTRACT        100       Desktop pre-game attract overlays
 * MOBILE_ATTRACT_VISUAL  500       Mobile attract blur/visual layer
 * GAMEPAD                1000      Gamepad controls + rotate prompt
 * ANIMATIONS             1100      Campaign animation overlays
 * MOBILE_ATTRACT_TOUCH   1200      Mobile attract touch-capture layer
 * PLAYER_HUD             2000      Always-visible gameplay UI
 * SIGNAL_QUALITY         2500      Connection-health HUD (above every cover)
 * DEBUG                  9999      Debug overlays + machine ID
 * GAMEPAD_MENU           99999     Gamepad options/exit strip (lib)
 * SETTINGS               100000+   Gamepad advanced settings (lib)
 * ─────────────────────────────────────────────────────────────────────
 *
 * NOTE: GAMEPAD_MENU and SETTINGS live inside the MobileGamepad library
 * and are listed here for reference only — do not change them here.
 */

// ── BACKGROUND ──────────────────────────────────────────────────────
// Page-level persistent backgrounds (seamless video, static fallback).
// Current: SeamlessBackground
export const Z_BACKGROUND = 0;

// ── STREAM ──────────────────────────────────────────────────────────
// The game stream video element and loading overlay content.
// Current: Player <video>, SeamlessLoadingOverlay content
export const Z_STREAM = 10;

// ── LOADING_UI ──────────────────────────────────────────────────────
// Chrome shown during the loading phase (above stream, below gameplay).
// Current: MachineId label (20), ScreenEdgeBorder (24), loading-phase wishlist (25)
export const Z_LOADING_UI = 20;
export const Z_LOADING_EDGE_BORDER = 24;
export const Z_LOADING_WISHLIST = 25;

// ── STREAM_STATUS ───────────────────────────────────────────────────
// Transient stream status indicators.
// Current: Pause icon, "Hold ESC to exit fullscreen" notification
export const Z_STREAM_STATUS = 50;

// ── RECONNECT ───────────────────────────────────────────────────────
// Connection retry overlay (spinner + message).
// Current: ReconnectingOverlay
export const Z_RECONNECT = 60;

// ── DESKTOP_ATTRACT ─────────────────────────────────────────────────
// Desktop-only pre-game attract overlays (WASD animation, Play Now card).
// Current: DesktopControlsAttractOverlay, PlayNowOverlay (legacy)
export const Z_DESKTOP_ATTRACT = 100;

// ── MOBILE_ATTRACT_VISUAL ───────────────────────────────────────────
// Mobile attract mode visual layer (blurred dark overlay with pulsing buttons).
// Current: MobileAttractOverlay blur layer
export const Z_MOBILE_ATTRACT_VISUAL = 500;

// ── GAMEPAD ─────────────────────────────────────────────────────────
// Gamepad controls, button layout, and rotate-to-landscape prompt.
// Current: MobileGamepadUiStandalone wrapper, RotateToLandscape
export const Z_GAMEPAD = 1000;

// ── ANIMATIONS ──────────────────────────────────────────────────────
// Campaign-configured animation overlays (fullscreen gate, join/idle).
// These sit above the gamepad so they visually cover it, but below
// the player HUD so CTA buttons and timers remain accessible.
// Current: MobileFullscreenAnimationOverlay, MobileJoinIdleAnimationOverlay
export const Z_ANIMATIONS = 1100;

// ── MOBILE_ATTRACT_TOUCH ────────────────────────────────────────────
// Transparent full-screen layer that captures the first touch/tap to
// trigger fullscreen on mobile attract mode.
// Current: MobileAttractOverlay interaction layer
export const Z_MOBILE_ATTRACT_TOUCH = 1200;

// ── PLAYER_HUD ──────────────────────────────────────────────────────
// Always-visible gameplay UI that must stay on top of all overlays
// and animations. Includes timers, CTA buttons, and screen effects.
// Current: CTA button wrapper, AnimatedWishlistButton, desktop timer, AfkOverlay vignette
export const Z_PLAYER_HUD = 2000;

// ── SIGNAL_QUALITY ──────────────────────────────────────────────────
// Connection-health HUD. Must sit above Z_RECONNECT: the reconnect cover
// is an opaque bg-black/95 full-bleed layer, so anything below it is
// invisible during exactly the window where connection health matters most.
// Current: SignalQualityIndicator
export const Z_SIGNAL_QUALITY = 2500;

// ── DEBUG ───────────────────────────────────────────────────────────
// Debug overlays and critical identifiers that must always be visible.
// Current: QuickstartPlayerDebugOverlay, MachineId label
export const Z_DEBUG = 9999;

// ── Library layers (reference only — do not use directly) ───────────
// GAMEPAD_MENU   = 99999     (ButtonMenu in @playcastdotio/MobileGamepad)
// SETTINGS       = 100000+   (AdvanceSettingsSidebar in @playcastdotio/MobileGamepad)
